/**
 * Баталгаа — `/warranty` модуль.
 *
 * Гурван таб: хайлт (серийн / утсаар) · нэхэмжлэл · хугацаа дуусах.
 *
 * ⚠️ Баталгааны бүх мэдээлэл `order_lines.snapshot`-оос — зарах мөчид
 *    ХӨЛДСӨН утга. Загвар, нийлүүлэгч дараа өөрчлөгдсөн ч энд
 *    нөлөөлөхгүй (CLAUDE.md дүрэм 5).
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatDate, formatMnt, formatQty, formatSerialDisplay, isValidSerial, moneyInput,
  normalizePhone,
  normalizeSerialInput,
} from '@boots/domain';
import { ApiError, api, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CLAIM_RESOLUTION_LABEL } from '../lib/types';
import {
  Button, Card, CardTitle, Empty, ErrorNote, Field, Input, Modal, OkNote,
  Select, Spinner, Tabs, Textarea,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';

interface Snapshot {
  serial: string; size: number; brand: string; modelName: string;
  colorway?: string; buyerName?: string; buyerPhone?: string;
  storeName?: string; sellerName?: string; supplierName?: string;
  soldAt: string;
}

interface ClaimBrief {
  id: string; code: string; resolution: string; reportedAt: string;
}

interface WarrantyLine {
  id: string;
  snapshot: Snapshot;
  warrantyDays: number;
  warrantyEndsAt: string;
  warranty: { state: 'ACTIVE' | 'EXPIRED'; endsAt: string; daysRemaining: number };
  order: { id: string; code: string; soldAt?: string };
  warrantyTerms?: { version: string } | null;
  claims: ClaimBrief[];
}

interface LookupResult {
  item: { id: string; serial: string | null; status: string };
  sold: boolean;
  line: WarrantyLine | null;
}

interface PhoneResult {
  customer: { id: string; phone: string; name: string | null };
  lines: WarrantyLine[];
}

interface ClaimRow {
  id: string;
  code: string;
  reportedAt: string;
  defectDescription: string | null;
  resolution: 'PENDING' | 'REPAIR' | 'EXCHANGE' | 'REFUND' | 'REJECTED';
  resolvedAt: string | null;
  repairCostMnt: string | null;
  refundMnt: string | null;
  defectType: { labelMn: string } | null;
  rejectReason: { labelMn: string } | null;
  resolvedBy: { fullName: string } | null;
  supplier: { code: string; name: string } | null;
  orderLine?: { snapshot: Snapshot } | null;
}

interface ExpiringRow {
  id: string;
  snapshot: Snapshot;
  warrantyEndsAt: string;
  order: { code: string; customer: { name: string | null; phone: string } | null };
}

const RESOLUTION_TONE: Record<string, string> = {
  PENDING: 'bg-warn-bg text-warn-ink',
  REPAIR: 'bg-accent-100 text-accent-700',
  EXCHANGE: 'bg-accent-100 text-accent-700',
  REFUND: 'bg-accent-100 text-accent-700',
  REJECTED: 'bg-alert-bg text-alert-ink',
};

type Tab = 'lookup' | 'claims' | 'expiring';

export function WarrantyPage() {
  const [tab, setTab] = useState<Tab>('lookup');

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'lookup', label: 'Хайлт' },
          { key: 'claims', label: 'Нэхэмжлэл' },
          { key: 'expiring', label: 'Хугацаа дуусах' },
        ]}
      />
      {tab === 'lookup' ? <LookupTab /> : tab === 'claims' ? <ClaimsTab /> : <ExpiringTab />}
    </div>
  );
}

// ------------------------------------------------------------ Хайлт
function LookupTab() {
  const [mode, setMode] = useState<'serial' | 'phone'>('serial');
  const [raw, setRaw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [bySerial, setBySerial] = useState<LookupResult | null>(null);
  const [byPhone, setByPhone] = useState<PhoneResult | null>(null);
  const [claimFor, setClaimFor] = useState<WarrantyLine | null>(null);
  const [busy, setBusy] = useState(false);

  async function search(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBySerial(null); setByPhone(null); setBusy(true);
    try {
      if (mode === 'serial') {
        const serial = normalizeSerialInput(raw);
        if (!isValidSerial(serial)) {
          setErr('Гутлын код буруу байна');
          return;
        }
        const res = await api<Envelope<LookupResult>>(`/warranty/lookup/${serial}`);
        setBySerial(res.data);
      } else {
        const phone = normalizePhone(raw) ?? raw.trim();
        const res = await api<Envelope<PhoneResult>>(`/warranty/lookup-by-phone/${encodeURIComponent(phone)}`);
        setByPhone(res.data);
      }
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Хайлт амжилтгүй');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <CardTitle right={
          <Tabs
            value={mode}
            onChange={(m) => { setMode(m); setRaw(''); setBySerial(null); setByPhone(null); setErr(null); }}
            tabs={[{ key: 'serial', label: 'Серийн дугаараар' }, { key: 'phone', label: 'Утсаар' }]}
          />
        }>
          Баталгаа шалгах
        </CardTitle>
        <form onSubmit={search} className="flex gap-2">
          <Input
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={mode === 'serial' ? '44609193703' : '99112233'}
            inputMode="numeric"
            autoFocus
            className="num text-base"
            aria-label={mode === 'serial' ? 'Серийн дугаар' : 'Утасны дугаар'}
          />
          <Button type="submit" disabled={busy} className="shrink-0">Хайх</Button>
        </form>
        {err ? <p className="mt-2 text-sm text-alert-ink">{err}</p> : null}
      </Card>

      {bySerial ? (
        bySerial.line ? (
          <WarrantyCard line={bySerial.line} onClaim={() => setClaimFor(bySerial.line)} />
        ) : (
          <Card>
            <p className="text-sm text-ink-2">
              Энэ гутал {bySerial.sold ? 'зарагдсан ч мөр олдсонгүй' : 'хараахан зарагдаагүй'} —
              баталгаа эхлээгүй байна. Төлөв: {bySerial.item.status}
            </p>
          </Card>
        )
      ) : null}

      {byPhone ? (
        <>
          <Card>
            <CardTitle>Худалдан авагч</CardTitle>
            <p className="text-sm">
              {byPhone.customer.name ?? '—'} · <span className="num">{byPhone.customer.phone}</span>
              <span className="ml-2 text-ink-2">{formatQty(byPhone.lines.length)} худалдан авалт</span>
            </p>
          </Card>
          {byPhone.lines.map((l) => (
            <WarrantyCard key={l.id} line={l} onClaim={() => setClaimFor(l)} />
          ))}
          {byPhone.lines.length === 0 ? <Empty>Худалдан авалт олдсонгүй</Empty> : null}
        </>
      ) : null}

      {claimFor ? (
        <NewClaim line={claimFor} onClose={() => setClaimFor(null)} />
      ) : null}
    </div>
  );
}

function WarrantyCard({ line, onClaim }: { line: WarrantyLine; onClaim: () => void }) {
  const { can, readOnly } = useAuth();
  const active = line.warranty.state === 'ACTIVE';
  const s = line.snapshot;
  const canClaim = can('SALESPERSON', 'STORE_MANAGER', 'WAREHOUSE', 'ADMIN') && !readOnly;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="num text-lg font-semibold">{formatSerialDisplay(s.serial)}</h3>
          <p className="mt-0.5 text-sm text-ink-2">
            {s.brand} · {s.modelName} · размер {s.size}
            {s.colorway ? ` · ${s.colorway}` : ''}
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs whitespace-nowrap ${
          active ? 'bg-accent-100 text-accent-700' : 'bg-alert-bg text-alert-ink'
        }`}>
          {active ? `Хүчинтэй · ${formatQty(line.warranty.daysRemaining)} хоног` : 'Хугацаа дууссан'}
        </span>
      </div>

      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <Row label="Захиалга" value={line.order.code} num />
        <Row label="Зарсан" value={formatDate(s.soldAt)} />
        <Row label="Баталгаа дуусах" value={formatDate(line.warrantyEndsAt)} />
        <Row label="Хугацаа" value={`${formatQty(line.warrantyDays)} хоног`} />
        <Row label="Худалдан авагч" value={s.buyerName ?? s.buyerPhone ?? '—'} />
        <Row label="Салбар" value={s.storeName ?? '—'} />
        <Row label="Нийлүүлэгч" value={s.supplierName ?? '—'} />
        {line.warrantyTerms ? <Row label="Нөхцөлийн хувилбар" value={line.warrantyTerms.version} /> : null}
      </dl>

      {line.claims.length > 0 ? (
        <div className="mt-3 border-t border-hair pt-3">
          <p className="mb-1.5 text-xs font-medium text-ink-2 uppercase">Нэхэмжлэлүүд</p>
          <ul className="flex flex-col gap-1 text-[13px]">
            {line.claims.map((c) => (
              <li key={c.id} className="flex items-center gap-2">
                <span className="num">{c.code}</span>
                <span className={`rounded-full px-2 py-[2px] text-[11px] ${RESOLUTION_TONE[c.resolution]}`}>
                  {CLAIM_RESOLUTION_LABEL[c.resolution]}
                </span>
                <span className="ml-auto text-ink-3">{formatDate(c.reportedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canClaim ? (
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" onClick={onClaim}>Нэхэмжлэл бүртгэх</Button>
        </div>
      ) : null}
    </Card>
  );
}

// ------------------------------------------------------------ Шинэ нэхэмжлэл
function NewClaim({ line, onClose }: { line: WarrantyLine; onClose: () => void }) {
  const qc = useQueryClient();
  const [defectLookupId, setDefect] = useState('');
  const [description, setDescription] = useState('');

  const defects = useQuery({
    queryKey: ['lookups', 'DEFECT_TYPE'],
    queryFn: () => api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=DEFECT_TYPE&active=true'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<Envelope<{ code: string }>>('/warranty/claims', {
        method: 'POST',
        body: {
          orderLineId: line.id,
          defectLookupId,
          ...(description.trim() ? { defectDescription: description.trim() } : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['claims'] });
      onClose();
    },
  });

  return (
    <Modal
      title="Баталгааны нэхэмжлэл"
      subtitle={`${formatSerialDisplay(line.snapshot.serial)} · ${line.snapshot.modelName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => create.mutate()} disabled={!defectLookupId || create.isPending}>
            {create.isPending ? 'Бүртгэж байна…' : 'Бүртгэх'}
          </Button>
        </>
      }
    >
      {line.warranty.state === 'EXPIRED' ? (
        <ErrorNote>Баталгааны хугацаа дууссан — сервер татгалзаж магадгүй.</ErrorNote>
      ) : null}
      <Field label="Гэмтлийн төрөл">
        <Select
          value={defectLookupId}
          onChange={setDefect}
          placeholder="Сонгох…"
          options={(defects.data?.data ?? []).map((d) => ({ value: d.id, label: d.labelMn }))}
        />
      </Field>
      <Field label="Тайлбар">
        <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      {create.error ? <ErrorNote>{(create.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ------------------------------------------------------------ Нэхэмжлэл
function ClaimsTab() {
  const [resolving, setResolving] = useState<ClaimRow | null>(null);
  const [filter, setFilter] = useState<'all' | 'PENDING' | 'done'>('all');

  const claims = useQuery({
    queryKey: ['claims'],
    queryFn: () => api<PagedEnvelope<ClaimRow>>('/warranty/claims?limit=100'),
  });

  if (claims.isPending) return <Spinner />;
  if (claims.error) return <ErrorNote>{(claims.error as Error).message}</ErrorNote>;

  const rows = claims.data.data;
  const filtered = rows.filter((c) =>
    filter === 'all' ? true : filter === 'PENDING' ? c.resolution === 'PENDING' : c.resolution !== 'PENDING',
  );

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={filter}
        onChange={setFilter}
        tabs={[
          { key: 'all', label: 'Бүгд', count: rows.length },
          { key: 'PENDING', label: 'Хүлээгдэж буй', count: rows.filter((c) => c.resolution === 'PENDING').length },
          { key: 'done', label: 'Шийдэгдсэн', count: rows.filter((c) => c.resolution !== 'PENDING').length },
        ]}
      />

      <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
        <DataTable
          rows={filtered}
          columns={claimColumns()}
          rowKey={(c) => c.id}
          onRowClick={(c) => { if (c.resolution === 'PENDING') setResolving(c); }}
          empty={<Empty>Нэхэмжлэл алга</Empty>}
        />
        <div className="px-card py-3 text-[11.5px] text-ink-3 sm:border-t sm:border-hair">
          Хүлээгдэж буй нэхэмжлэл дээр дарж шийдвэрлэнэ
        </div>
      </section>

      {resolving ? <ResolveClaim claim={resolving} onClose={() => setResolving(null)} /> : null}
    </div>
  );
}

function ResolveClaim({ claim, onClose }: { claim: ClaimRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [resolution, setResolution] = useState<'REPAIR' | 'EXCHANGE' | 'REFUND' | 'REJECTED'>('REPAIR');
  const [repairCostMnt, setRepair] = useState('');
  const [refundMnt, setRefund] = useState('');
  const [rejectReasonId, setReject] = useState('');
  const [newItemSerial, setNewSerial] = useState('');
  const [newItemId, setNewItemId] = useState('');
  const [lookupErr, setLookupErr] = useState<string | null>(null);

  const reasons = useQuery({
    queryKey: ['lookups', 'CLAIM_REJECT_REASON'],
    queryFn: () => api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=CLAIM_REJECT_REASON&active=true'),
    enabled: resolution === 'REJECTED',
  });

  async function findReplacement() {
    setLookupErr(null); setNewItemId('');
    const serial = normalizeSerialInput(newItemSerial);
    if (!isValidSerial(serial)) { setLookupErr('Серийн дугаар буруу'); return; }
    try {
      const res = await api<Envelope<{ id: string; status: string }>>(`/items/by-serial/${serial}`);
      if (res.data.status !== 'AVAILABLE') { setLookupErr(`Солих гутал зарагдах боломжгүй (${res.data.status})`); return; }
      setNewItemId(res.data.id);
    } catch (e) {
      setLookupErr(e instanceof ApiError ? e.message : 'Олдсонгүй');
    }
  }

  const resolve = useMutation({
    mutationFn: () =>
      api(`/warranty/claims/${claim.id}/resolve`, {
        method: 'POST',
        body: {
          resolution,
          ...(resolution === 'REPAIR' && repairCostMnt ? { repairCostMnt: moneyInput(repairCostMnt) } : {}),
          ...(resolution === 'REFUND' && refundMnt ? { refundMnt: moneyInput(refundMnt) } : {}),
          ...(resolution === 'EXCHANGE' && newItemId ? { newItemId } : {}),
          ...(resolution === 'REJECTED' && rejectReasonId ? { rejectReasonId } : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['claims'] });
      onClose();
    },
  });

  const ready =
    resolution === 'EXCHANGE' ? !!newItemId
      : resolution === 'REJECTED' ? !!rejectReasonId
        : true;

  return (
    <Modal
      title={`Нэхэмжлэл ${claim.code}`}
      subtitle={claim.defectType?.labelMn ?? undefined}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => resolve.mutate()} disabled={!ready || resolve.isPending}>
            {resolve.isPending ? 'Шийдвэрлэж байна…' : 'Шийдвэрлэх'}
          </Button>
        </>
      }
    >
      {claim.defectDescription ? (
        <p className="rounded-card bg-well px-3 py-2 text-[13px] text-ink-2">{claim.defectDescription}</p>
      ) : null}

      <Field label="Шийдвэр">
        <Select
          value={resolution}
          onChange={(v) => setResolution(v as typeof resolution)}
          options={[
            { value: 'REPAIR', label: 'Засвар' },
            { value: 'EXCHANGE', label: 'Солих' },
            { value: 'REFUND', label: 'Мөнгө буцаах' },
            { value: 'REJECTED', label: 'Татгалзах' },
          ]}
        />
      </Field>

      {resolution === 'REPAIR' ? (
        <Field label="Засварын зардал (₮)">
          <Input value={repairCostMnt} onChange={(e) => setRepair(e.target.value)} inputMode="decimal" className="num text-right" />
        </Field>
      ) : null}

      {resolution === 'REFUND' ? (
        <Field label="Буцаах дүн (₮)">
          <Input value={refundMnt} onChange={(e) => setRefund(e.target.value)} inputMode="decimal" className="num text-right" />
        </Field>
      ) : null}

      {resolution === 'EXCHANGE' ? (
        <>
          <Field label="Солих гутлын серийн дугаар">
            <div className="flex gap-2">
              <Input value={newItemSerial} onChange={(e) => setNewSerial(e.target.value)} inputMode="numeric" className="num" />
              <Button variant="ghost" onClick={() => void findReplacement()} className="shrink-0">Шалгах</Button>
            </div>
          </Field>
          {newItemId ? <OkNote>Солих гутал бэлэн</OkNote> : null}
          {lookupErr ? <ErrorNote>{lookupErr}</ErrorNote> : null}
          <p className="text-[11.5px] text-ink-3">
            Сольсон гутлын баталгаа үлдсэн хугацаагаараа үргэлжилнэ (CONTINUE).
          </p>
        </>
      ) : null}

      {resolution === 'REJECTED' ? (
        <Field label="Татгалзах шалтгаан">
          <Select
            value={rejectReasonId}
            onChange={setReject}
            placeholder="Сонгох…"
            options={(reasons.data?.data ?? []).map((r) => ({ value: r.id, label: r.labelMn }))}
          />
        </Field>
      ) : null}

      {resolve.error ? <ErrorNote>{(resolve.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ------------------------------------------------------------ Хугацаа дуусах
function ExpiringTab() {
  const [days, setDays] = useState('30');

  const q = useQuery({
    queryKey: ['warranty-expiring', days],
    queryFn: () => api<{ data: ExpiringRow[]; meta?: { count: number } }>(`/warranty/expiring?days=${days}`),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] text-ink-2">Дараах хугацаанд дуусах:</span>
        {['30', '60', '90', '365'].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-full px-[11px] py-1 text-xs transition ${
              days === d ? 'bg-accent-100 text-accent-700' : 'border border-hair bg-panel text-ink-2 hover:bg-accent-100/50'
            }`}
          >
            {d} хоног
          </button>
        ))}
      </div>

      {q.isPending ? <Spinner /> : q.error ? (
        <ErrorNote>{(q.error as Error).message}</ErrorNote>
      ) : (
        <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
          <DataTable
            rows={q.data.data}
            columns={expiringColumns()}
            rowKey={(r) => r.id}
            empty={<Empty>Энэ хугацаанд дуусах баталгаа алга</Empty>}
          />
        </section>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Туслах

function Row({ label, value, num = false }: { label: string; value: string; num?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-2">{label}</dt>
      <dd className={num ? 'num' : ''}>{value}</dd>
    </div>
  );
}


/** Баталгааны нэхэмжлэл — 640px-ээс доош нэхэмжлэл тус бүрийн карт. */
function claimColumns(): Column<ClaimRow>[] {
  return [
    { key: 'code', header: 'Дугаар', mobile: 'title', cell: (c) => <span className="num">{c.code}</span> },
    {
      key: 'item',
      header: 'Гутал',
      cell: (c) =>
        c.orderLine?.snapshot ? (
          <span className="num text-ink-2">{formatSerialDisplay(c.orderLine.snapshot.serial)}</span>
        ) : (
          <span className="text-ink-2">—</span>
        ),
    },
    // Гэмтлийн кирилл нэр — боогдоно
    { key: 'defect', header: 'Гэмтэл', cell: (c) => <span className="text-ink-2 text-pretty">{c.defectType?.labelMn ?? '—'}</span> },
    {
      key: 'resolution',
      header: 'Шийдвэр',
      cell: (c) => (
        <span className={`inline-block rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${RESOLUTION_TONE[c.resolution]}`}>
          {CLAIM_RESOLUTION_LABEL[c.resolution]}
        </span>
      ),
    },
    {
      key: 'cost',
      header: 'Зардал',
      align: 'right',
      mobile: 'value',
      cell: (c) =>
        c.repairCostMnt ? formatMnt(c.repairCostMnt) : c.refundMnt ? formatMnt(c.refundMnt) : '—',
    },
    {
      key: 'at',
      header: 'Бүртгэсэн',
      align: 'right',
      cell: (c) => <span className="text-ink-2">{formatDate(c.reportedAt)}</span>,
    },
  ];
}

/** Дуусах гэж буй баталгаа — 640px-ээс доош гутал тус бүрийн карт. */
function expiringColumns(): Column<ExpiringRow>[] {
  return [
    {
      key: 'serial',
      header: 'Серийн дугаар',
      mobile: 'title',
      cell: (r) => <span className="num">{formatSerialDisplay(r.snapshot.serial)}</span>,
    },
    {
      key: 'model',
      header: 'Загвар',
      cell: (r) => <span className="text-ink-2 text-pretty">{r.snapshot.brand} · {r.snapshot.modelName}</span>,
    },
    {
      key: 'buyer',
      header: 'Худалдан авагч',
      cell: (r) => (
        <span className="text-ink-2 text-pretty">
          {r.order.customer?.name ?? r.order.customer?.phone ?? r.snapshot.buyerName ?? '—'}
        </span>
      ),
    },
    { key: 'order', header: 'Захиалга', cell: (r) => <span className="num">{r.order.code}</span> },
    {
      key: 'ends',
      header: 'Дуусах',
      align: 'right',
      mobile: 'value',
      cell: (r) => <span className="text-ink-2">{formatDate(r.warrantyEndsAt)}</span>,
    },
  ];
}
